import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@iptv/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { adminFetch, type ApiError } from '../lib/api';
import { useTotpChallenge } from '../lib/totp';
import { SortableTh, useTableSort } from '../lib/useTableSort';

interface PlanRow {
  id: number;
  site_id: number | null;
  code: string;
  name: string;
  description: string | null;
  duration_days: number;
  connections: number;
  base_price_cents: number;
  anchor_price_cents: number | null;
  currency: string;
  features: string[];
  badge: string | null;
  sort_order: number;
  active: boolean;
}

interface SiteAdmin {
  id: number;
  slug: string;
}

interface PlanForm {
  site_id: number | null;
  code: string;
  name: string;
  description: string;
  duration_days: number;
  connections: number;
  base_price_cents: number;
  anchor_price_cents: number | null;
  currency: string;
  features: string;
  badge: string;
  sort_order: number;
}

const EMPTY_FORM: PlanForm = {
  site_id: null,
  code: '',
  name: '',
  description: '',
  duration_days: 30,
  connections: 1,
  base_price_cents: 2999,
  anchor_price_cents: null,
  currency: 'USD',
  features: '',
  badge: '',
  sort_order: 0,
};

function formatPrice(cents: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
}

function planToForm(p: PlanRow): PlanForm {
  return {
    site_id: p.site_id,
    code: p.code,
    name: p.name,
    description: p.description ?? '',
    duration_days: p.duration_days,
    connections: p.connections,
    base_price_cents: p.base_price_cents,
    anchor_price_cents: p.anchor_price_cents,
    currency: p.currency,
    features: p.features.join('\n'),
    badge: p.badge ?? '',
    sort_order: p.sort_order,
  };
}

function formToPayload(f: PlanForm): Record<string, unknown> {
  return {
    site_id: f.site_id,
    code: f.code.trim(),
    name: f.name.trim(),
    description: f.description.trim() || null,
    duration_days: Number(f.duration_days),
    connections: Number(f.connections),
    base_price_cents: Number(f.base_price_cents),
    anchor_price_cents: f.anchor_price_cents ? Number(f.anchor_price_cents) : null,
    currency: f.currency.trim().toUpperCase() || 'USD',
    features: f.features
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean),
    badge: f.badge.trim() || null,
    sort_order: Number(f.sort_order),
  };
}

export function PlansPage() {
  const queryClient = useQueryClient();
  const challengeTotp = useTotpChallenge();

  const [editing, setEditing] = useState<PlanRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<PlanForm>(EMPTY_FORM);
  const [filterSite, setFilterSite] = useState<string>('');

  const plans = useQuery<PlanRow[]>({
    queryKey: ['admin', 'plans'],
    queryFn: () => adminFetch<PlanRow[]>('/api/v1/admin/plans'),
  });

  const sites = useQuery({
    queryKey: ['admin', 'sites'],
    queryFn: () => adminFetch<SiteAdmin[]>('/api/v1/admin/sites'),
  });

  const create = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      adminFetch<PlanRow>('/api/v1/admin/plans', { method: 'POST', body: payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] });
      setCreating(false);
      setForm(EMPTY_FORM);
    },
  });

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Record<string, unknown> }) =>
      adminFetch<PlanRow>(`/api/v1/admin/plans/${id}`, { method: 'PATCH', body: payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] });
      setEditing(null);
    },
  });

  const remove = useMutation({
    mutationFn: ({ id, token }: { id: number; token: string }) =>
      adminFetch(`/api/v1/admin/plans/${id}?totp_challenge_token=${encodeURIComponent(token)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] }),
  });

  function openCreate() {
    setForm(EMPTY_FORM);
    setCreating(true);
  }

  function openEdit(p: PlanRow) {
    setForm(planToForm(p));
    setEditing(p);
  }

  async function handleDelete(plan: PlanRow) {
    const token = await challengeTotp({
      message: `Delete plan "${plan.name}" (${plan.code}). It will be hidden from the public site. This cannot be undone from the admin UI.`,
    });
    if (!token) return;
    try {
      await remove.mutateAsync({ id: plan.id, token });
    } catch (err) {
      alert((err as ApiError).detail ?? 'Delete failed');
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload = formToPayload(form);
    if (editing) {
      await update.mutateAsync({ id: editing.id, payload });
    } else {
      await create.mutateAsync(payload);
    }
  }

  const visible = (plans.data ?? []).filter((p) => {
    if (!filterSite) return true;
    if (filterSite === 'global') return p.site_id === null;
    return String(p.site_id) === filterSite;
  });
  const { sorted, sortKey, sortDir, toggleSort } = useTableSort<PlanRow>(visible, {
    initialKey: 'sort_order',
    initialDir: 'asc',
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Label htmlFor="site-filter" className="text-sm text-muted-foreground">
            Site:
          </Label>
          <select
            id="site-filter"
            value={filterSite}
            onChange={(e) => setFilterSite(e.target.value)}
            className="rounded-md border bg-background px-2 py-1 text-sm"
          >
            <option value="">All</option>
            <option value="global">Global (no site)</option>
            {sites.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.slug}
              </option>
            ))}
          </select>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4" /> New plan
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50">
            <tr>
              <SortableTh sortKey="code" current={sortKey} dir={sortDir} onSort={toggleSort}>
                Code
              </SortableTh>
              <SortableTh sortKey="name" current={sortKey} dir={sortDir} onSort={toggleSort}>
                Name
              </SortableTh>
              <SortableTh sortKey="site_id" current={sortKey} dir={sortDir} onSort={toggleSort}>
                Site
              </SortableTh>
              <SortableTh
                sortKey="duration_days"
                current={sortKey}
                dir={sortDir}
                onSort={toggleSort}
              >
                Days
              </SortableTh>
              <SortableTh sortKey="connections" current={sortKey} dir={sortDir} onSort={toggleSort}>
                Conn.
              </SortableTh>
              <SortableTh
                sortKey="anchor_price_cents"
                current={sortKey}
                dir={sortDir}
                onSort={toggleSort}
              >
                Anchor
              </SortableTh>
              <SortableTh
                sortKey="base_price_cents"
                current={sortKey}
                dir={sortDir}
                onSort={toggleSort}
              >
                Price
              </SortableTh>
              <SortableTh sortKey="active" current={sortKey} dir={sortDir} onSort={toggleSort}>
                Active
              </SortableTh>
              <th className="px-3 py-2 text-right text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {plans.isPending && (
              <tr>
                <td colSpan={9} className="px-3 py-4 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {visible.length === 0 && !plans.isPending && (
              <tr>
                <td colSpan={9} className="px-3 py-4 text-center text-muted-foreground">
                  No plans match the filter.
                </td>
              </tr>
            )}
            {sorted.map((p) => {
              const siteSlug = sites.data?.find((s) => s.id === p.site_id)?.slug ?? 'global';
              return (
                <tr key={p.id} className="border-t">
                  <td className="px-3 py-2 font-mono text-xs">{p.code}</td>
                  <td className="px-3 py-2">
                    {p.name}
                    {p.badge && (
                      <Badge variant="hot" className="ml-2">
                        {p.badge}
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{siteSlug}</td>
                  <td className="px-3 py-2">{p.duration_days}</td>
                  <td className="px-3 py-2 text-center">{p.connections}</td>
                  <td className="px-3 py-2 text-muted-foreground line-through">
                    {p.anchor_price_cents ? formatPrice(p.anchor_price_cents, p.currency) : '—'}
                  </td>
                  <td className="px-3 py-2 font-medium">
                    {formatPrice(p.base_price_cents, p.currency)}
                  </td>
                  <td className="px-3 py-2">
                    {p.active ? (
                      <Badge variant="success">yes</Badge>
                    ) : (
                      <Badge variant="secondary">no</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(p)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(p)}
                        disabled={remove.isPending}
                        title={
                          p.active
                            ? 'Soft-delete this plan'
                            : 'Already deleted — click to delete again'
                        }
                      >
                        <Trash2
                          className={`h-3.5 w-3.5 ${p.active ? '' : 'text-muted-foreground'}`}
                        />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog
        open={creating || editing !== null}
        onOpenChange={(open) => {
          if (!open) {
            setCreating(false);
            setEditing(null);
          }
        }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.name}` : 'New plan'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="site_id">Site (empty = global)</Label>
              <select
                id="site_id"
                value={form.site_id ?? ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    site_id: e.target.value ? Number(e.target.value) : null,
                  })
                }
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">Global</option>
                {sites.data?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.slug}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="code">Code (unique per site)</Label>
              <Input
                id="code"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                disabled={editing !== null}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="duration_days">Duration (days)</Label>
              <Input
                id="duration_days"
                type="number"
                min={1}
                value={form.duration_days}
                onChange={(e) => setForm({ ...form, duration_days: Number(e.target.value) })}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="connections">Connections</Label>
              <Input
                id="connections"
                type="number"
                min={1}
                value={form.connections}
                onChange={(e) => setForm({ ...form, connections: Number(e.target.value) })}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="base_price_cents">Price (cents)</Label>
              <Input
                id="base_price_cents"
                type="number"
                min={0}
                value={form.base_price_cents}
                onChange={(e) => setForm({ ...form, base_price_cents: Number(e.target.value) })}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="anchor_price_cents">Anchor price (cents, opt)</Label>
              <Input
                id="anchor_price_cents"
                type="number"
                min={0}
                value={form.anchor_price_cents ?? ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    anchor_price_cents: e.target.value ? Number(e.target.value) : null,
                  })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="currency">Currency</Label>
              <Input
                id="currency"
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                maxLength={3}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="badge">Badge (opt)</Label>
              <Input
                id="badge"
                value={form.badge}
                placeholder="BEST VALUE"
                onChange={(e) => setForm({ ...form, badge: e.target.value })}
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="features">Features (one per line)</Label>
              <textarea
                id="features"
                rows={4}
                value={form.features}
                onChange={(e) => setForm({ ...form, features: e.target.value })}
                className="w-full rounded-md border border-input bg-background p-2 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sort_order">Sort order</Label>
              <Input
                id="sort_order"
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })}
              />
            </div>
            {(create.isError || update.isError) && (
              <div className="md:col-span-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {String(
                  (create.error as ApiError | null)?.detail ??
                    (update.error as ApiError | null)?.detail ??
                    'Something went wrong',
                )}
              </div>
            )}
            <div className="md:col-span-2 flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setCreating(false);
                  setEditing(null);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending || update.isPending}>
                {create.isPending || update.isPending
                  ? 'Saving…'
                  : editing
                    ? 'Save changes'
                    : 'Create plan'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
